import expandRecurringOccurrences from './expandRecurringOccurrences'
import findAbsence, { intervalsOverlap, absenceTimeLabel } from './findAbsence'

const WEEKDAY_KEYS = [
    'sunday', 'monday', 'tuesday', 'wednesday',
    'thursday', 'friday', 'saturday',
]

const startOfDay = (date) => {
    const d = new Date(date)
    d.setHours(0, 0, 0, 0)
    return d
}

// Day-level effective check. Effective if:
//   !EffectiveFrom OR EffectiveFrom <= start of windowDay
// AND
//   !EffectiveUntil OR EffectiveUntil >= start of windowDay
const isRowEffectiveOnDate = (row, windowDay) => {
    const day = startOfDay(windowDay).getTime()
    if (row.EffectiveFrom && typeof row.EffectiveFrom.toDate === 'function') {
        const from = startOfDay(row.EffectiveFrom.toDate()).getTime()
        if (from > day) return false
    }
    if (row.EffectiveUntil && typeof row.EffectiveUntil.toDate === 'function') {
        const until = startOfDay(row.EffectiveUntil.toDate()).getTime()
        if (until < day) return false
    }
    return true
}

// Build an interval [start, end] on `dateOnDay` from a row's StartTime/EndTime.
const rowIntervalOnDate = (row, dateOnDay) => {
    if (!row.StartTime || !row.EndTime) return null
    const [sh, sm] = row.StartTime.split(':').map(Number)
    const [eh, em] = row.EndTime.split(':').map(Number)
    if (!Number.isFinite(sh) || !Number.isFinite(eh)) return null
    const start = new Date(dateOnDay)
    start.setHours(sh, sm || 0, 0, 0)
    const end = new Date(dateOnDay)
    end.setHours(eh, em || 0, 0, 0)
    return { start, end }
}

// Returns:
//   null  — persona has no schedule rows at all (skip outside-hours check)
//   true  — at least one effective row covers the window's weekday + time
//   false — persona is configured but the window falls outside their hours
const checkInWorkingHours = (memberId, schedule, windowStart, windowEnd) => {
    if (!Array.isArray(schedule) || schedule.length === 0) return null

    const personaRows = schedule.filter((r) => r && r.PersonaID === memberId)
    if (personaRows.length === 0) return null

    const dayKey = WEEKDAY_KEYS[windowStart.getDay()]
    const effectiveRows = personaRows.filter((r) => isRowEffectiveOnDate(r, windowStart))
    if (effectiveRows.length === 0) return false

    for (const row of effectiveRows) {
        if (row.Day !== dayKey) continue
        const interval = rowIntervalOnDate(row, windowStart)
        if (!interval) continue
        if (intervalsOverlap(windowStart, windowEnd, interval.start, interval.end)) {
            return true
        }
    }
    return false
}

const computeMemberAvailability = ({
    memberIds,
    tasks,
    absences,
    schedule,
    currentTaskId,
    windowStart,
    windowEnd,
}) => {
    const result = {}

    if (!windowStart || !windowEnd) {
        memberIds.forEach((id) => {
            result[id] = { status: 'no-task-time' }
        })
        return result
    }

    const candidateTasks = (tasks || []).filter(
        (t) => t && t.docid !== currentTaskId
    )

    memberIds.forEach((memberId) => {
        const absence = findAbsence(absences, memberId, windowStart, windowEnd)
        if (absence) {
            // `times` is leeg zodra de afwezigheid hele dagen beslaat, en dan
            // toont de badge alleen de reden — zoals hiervoor.
            result[memberId] = {
                status: 'absent',
                reason: absence.Reason || 'overig',
                times: absenceTimeLabel(absence),
            }
            return
        }

        const inHours = checkInWorkingHours(memberId, schedule, windowStart, windowEnd)
        if (inHours === false) {
            result[memberId] = { status: 'outside-hours' }
            return
        }

        const memberTasks = candidateTasks.filter(
            (t) => Array.isArray(t.TeamMembers) && t.TeamMembers.includes(memberId)
        )

        let conflict = null

        for (const task of memberTasks) {
            const intervals = expandRecurringOccurrences(task, windowStart, windowEnd)
            const hit = intervals.find(({ start, end }) =>
                intervalsOverlap(windowStart, windowEnd, start, end)
            )
            if (hit) {
                conflict = {
                    taskId: task.docid,
                    title: task.Title || '',
                    start: hit.start,
                    end: hit.end,
                }
                break
            }
        }

        result[memberId] = conflict
            ? { status: 'busy', conflict }
            : { status: 'free' }
    })

    return result
}

export default computeMemberAvailability
